import { apiClient } from '../apiClient';

/**
 * Integrity as it reaches beyond the trainer.
 *
 * Two things an administrator wants: can I believe the readiness numbers, and
 * is there anything here I personally have to deal with. The first is served by
 * aggregates with no names in them, the second by escalations — the only named
 * records that arrive unprompted, and only because a trainer who spoke to the
 * student decided this one was worth an administrator's time.
 */

/** One case a trainer raised. */
export interface Escalation {
  id: string;
  batch_id: string;
  batch_name: string;
  student_id: string;
  student_name: string;
  assessment_id?: string;
  assessment_title?: string;
  /** The machine's word for what was seen — the footnote, never the headline. */
  reason?: string;
  /** The trainer's sentence. This is what the row leads with. */
  note: string;
  raised_by: string;
  raised_at: string;
  acknowledged_by?: string;
  acknowledged_at?: string;
  acknowledged_note?: string;
}

/**
 * What we can and cannot account for.
 *
 * The first block a TPO reads, on purpose. Every competing dashboard reports
 * total confidence about things it cannot see; leading with the gap is the
 * cheapest credibility available.
 */
export interface IntegrityCoverage {
  practice_attempts: number;
  practice_recorded: number;
  repos_expected: number;
  repos_read: number;
}

export interface IntegrityComposition {
  written_here: number;
  part_pasted: number;
  arrived_whole: number;
  built_over_time: number;
  one_sitting: number;
  one_commit: number;
  /** Students touched by any match — groups, not pairs. */
  similar_groups: number;
}

/** What was done about it. The block a TPO actually reads. */
export interface IntegrityHandlingCounts {
  raised: number;
  resolved: number;
  escalated: number;
  unresolved: number;
}

export interface IntegritySummary {
  coverage: IntegrityCoverage;
  composition: IntegrityComposition;
  handling: IntegrityHandlingCounts;
  /** Window for the handling counts. Coverage and composition are point-in-time. */
  since_days: number;
}

/** The percentage of practice work we have a record of, 0 when there is none. */
export function practiceCoveragePct(c: IntegrityCoverage): number {
  if (!c.practice_attempts) return 0;
  return (c.practice_recorded * 100) / c.practice_attempts;
}

export const integrityApi = {
  getSummary: () => apiClient.get<IntegritySummary>('/api/v1/org/integrity/summary'),

  listEscalations: (includeAcknowledged = false) =>
    apiClient.get<Escalation[]>('/api/v1/org/integrity/escalations', {
      params: includeAcknowledged ? { include_acknowledged: 'true' } : undefined,
    }),

  acknowledge: (id: string, note?: string) =>
    apiClient.post<{ status: string }>(`/api/v1/org/integrity/escalations/${id}/acknowledge`, {
      note,
    }),

  /** Trainer-only: send one case on to the TPO. The note is required. */
  raise: (
    batchId: string,
    body: { student_id: string; assessment_id?: string; reason?: string; note: string },
  ) =>
    apiClient.post<{ id: string }>(`/api/v1/trainer/integrity/batches/${batchId}/escalate`, body),
};
